import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { Pagination } from '@/components/ui/Pagination'
import DealerClosedTag from '@/pages/RoIntake/components/DealerClosedTag'
import { roHref } from '@/pages/RoIntake/roLinks'
import AttachmentsPopover from '@/pages/WorkQueue/AttachmentsPopover'
import {
  dispositionsFor,
  DISPOSITION,
  formatDuration,
  savesInPlace,
} from '@/pages/WorkQueue/queue'
import { DealerClosedSwitch, RowSaveButton, StatusChips } from '@/pages/WorkQueue/RowControls'

import {
  BUCKET,
  BUCKET_META,
  bucketMeta,
  bucketsForRo,
  IN_PROGRESS_BUCKETS,
  OLDER_PERIOD,
  OUTSTANDING_BUCKETS,
  PERIODS,
  SLA_LATE,
} from './buckets'

const SLA_HOURS = 24
const WRITTEN_OFF_LINE = 'Resolved - Written Off'

const money = (value) => {
  const amount = Number(value)
  if (!amount) return ''
  return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

const daysSinceReceived = (ro) => dayjs().diff(dayjs(ro.receivedAt), 'day')
const hoursSinceReceived = (ro) => dayjs().diff(dayjs(ro.receivedAt), 'hour')

/**
 * Late is Ready or Closed past the SLA, counted from receipt. Each must move on within a
 * day: Closed to the Outstanding List or to Closed and submitted (WPC, 2026-09-28).
 * Leaving either clears it.
 */
const SLA_STATUSES = ['Not Started', 'Closed']
const isLate = (ro) => SLA_STATUSES.includes(ro.status) && hoursSinceReceived(ro) >= SLA_HOURS

const liveLineItems = (ro) => (ro.lineItems || []).filter((item) => !item.isRemoved)

/**
 * The RO's note. WARP has no reason codes or free-text RO notes, but the open actions
 * needed on its Outstanding List line items are the same thing Mark's prototype shows
 * there ("Missing hard copy", "Prior approval not on file").
 */
const openActions = (ro) => {
  const names = liveLineItems(ro).flatMap((item) =>
    (item.actionsNeeded || [])
      .filter((need) => !need.completedAt && need.action?.name)
      .map((need) => need.action.name),
  )
  return [...new Set(names)]
}

/**
 * Dollars written off. `totalClaimAmount` leaves written-off line items out, so this is
 * the part of the claim that was lost. WARP records no paid amount to show beside it.
 */
const writtenOff = (ro) =>
  liveLineItems(ro)
    .filter((item) => item.state === WRITTEN_OFF_LINE)
    .reduce((sum, item) => sum + (Number(item.claimAmount) || 0), 0)

/**
 * The status pills for one RO, in the table's own colors. A pill the panel header
 * already shows is true of every row, so the row leaves it out. "Closed and submitted"
 * never shows either: every RO in it also carries the pill for where it stands now
 * (Pending OEM, Paid, ...), which says it was submitted.
 */
const pillsFor = (ro, selectedBucket) =>
  bucketsForRo(ro).filter((bucket) => bucket !== selectedBucket && bucket !== BUCKET.SUBMITTED)

/**
 * Where a returned RO is now, for the Returned list's Back in column. Every row in that
 * list is Returned, so the header says that; the row says which stage it went back to.
 */
const RETURNED_TO = {
  'Not Started': 'Ready',
  'On Outstanding List': 'Outstanding List',
  Closed: 'Closed',
}

const Pill = ({ bucket }) => {
  const meta = BUCKET_META[bucket]
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: meta.fill, color: meta.ink }}
    >
      {meta.label}
    </span>
  )
}

Pill.propTypes = {
  bucket: PropTypes.string.isRequired,
}

/**
 * The lists whose rows hold more than one status, so they keep a Status column (Emily,
 * 2026-10-01). In every other list the panel header names the one status all rows share,
 * and a column would repeat it.
 */
const MIXED_STATUS_BUCKETS = [BUCKET.SUBMITTED, BUCKET.RECEIVED, BUCKET.INTERNAL]

/** Outstanding (pre-submission) and its two review lists. */
const REVIEW_BUCKETS = [BUCKET.OUTSTANDING, ...OUTSTANDING_BUCKETS]

/**
 * Column widths, in pixels. Every column has one, so a list with many columns grows wider
 * than the panel and scrolls sideways, instead of squeezing its text into slivers. A list
 * narrower than the panel stretches to fill it.
 */
const COLUMN_WIDTHS = {
  ro: 128,
  status: 152,
  backIn: 112,
  review: 104,
  actions: 176,
  age: 72,
  claimed: 88,
  moveTo: 184,
  dealerClosed: 64,
  files: 64,
  save: 76,
}

/**
 * The one list with a Move to column: Ready. WPC moves ROs from the Work Queue's tables,
 * every queue there; on the dashboard, only Ready (2026-10-12). Every list, Received too,
 * keeps the dealer-closed flag and attachments.
 */
const MOVE_BUCKETS = [BUCKET.READY]

/**
 * The sortable columns, by the API ordering field they sort on. `first` is the direction
 * a column sorts in when first clicked. Age sorts on received_at, so its arrow is flipped:
 * oldest received first is largest age first.
 */
const SORTS = {
  ro: { field: 'ro_number', first: 'asc' },
  status: { field: 'status', first: 'asc' },
  age: { field: 'received_at', first: 'asc', flipped: true },
  claimed: { field: 'total_claim_amount', first: 'desc' },
}

const SortHeader = ({ align, children, className, column, onSortChange, orderBy }) => {
  const { field, first, flipped } = SORTS[column]
  const active = orderBy.replace(/^-/, '') === field
  const ascending = active && !orderBy.startsWith('-')
  // What the column's own values are doing, which for Age is the reverse of the field.
  const valuesAscending = flipped ? !ascending : ascending

  const next = () => {
    if (!active) return first === 'asc' ? field : `-${field}`
    return ascending ? `-${field}` : field
  }

  let ariaSort = 'none'
  if (active) ariaSort = valuesAscending ? 'ascending' : 'descending'

  return (
    <th aria-sort={ariaSort} className={mergeClassNames('py-2 font-bold', className)}>
      <button
        className={mergeClassNames(
          'hover:text-charcoal-900 group inline-flex items-center gap-0.5 uppercase tracking-widest',
          align === 'right' && 'flex-row-reverse',
        )}
        onClick={() => onSortChange(next())}
        type="button"
      >
        {children}
        {active && valuesAscending && <ChevronUpIcon aria-hidden="true" className="h-3.5 w-3.5" />}
        {active && !valuesAscending && (
          <ChevronDownIcon aria-hidden="true" className="h-3.5 w-3.5" />
        )}
        {!active && (
          <ChevronDownIcon
            aria-hidden="true"
            className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-40"
          />
        )}
      </button>
    </th>
  )
}

SortHeader.propTypes = {
  align: PropTypes.oneOf(['left', 'right']),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  column: PropTypes.oneOf(Object.keys(SORTS)).isRequired,
  onSortChange: PropTypes.func.isRequired,
  orderBy: PropTypes.string.isRequired,
}

SortHeader.defaultProps = {
  align: 'left',
  className: '',
}

/**
 * The repair orders behind one number. Sticky beside the matrix on a wide screen,
 * stacked beneath it on a narrow one.
 *
 * In-progress buckets open with the aging strip, oldest first — the question people
 * actually have about a pile of open work is how old the bottom of it is.
 */
const RoListPanel = ({
  agingCounts,
  dealerId,
  lateTotal,
  loading,
  onClose,
  onPageChange,
  onOpenOutstanding,
  onOpenRo,
  permissions,
  rowEdits,
  workQueueLinks,
  onSortChange,
  orderBy,
  page,
  pageSize,
  rows,
  selected,
  selfManaged,
  showWpcColumns,
  total,
}) => {
  // Edits staged on rows wait for Save or Save all: the same explicit save as the Work
  // Queue (2026-09-17: a wrong click should not stick). The page owns them, so Manage
  // Repair Order can pick them up.
  const { commit, editFor, rowsToSave, saving, updateEdit } = rowEdits
  const [savingAll, setSavingAll] = useState(false)

  if (!selected) {
    return (
      <div className="rounded-2xl bg-white p-10 text-center shadow-sm">
        <p className="text-charcoal-400 text-sm">
          Pick any number in the table to see the repair orders behind it.
        </p>
      </div>
    )
  }

  const meta = bucketMeta(selected.bucket, { selfManaged })
  const period = [...PERIODS, OLDER_PERIOD].find((p) => p.id === selected.periodId)
  // Counted across the whole cell by the page, not from the rows on screen, which are
  // one page of it.
  const showAging = IN_PROGRESS_BUCKETS.includes(selected.bucket)
  const bands = showAging && agingCounts ? agingCounts.filter(({ count }) => count > 0) : null
  const lateCount = lateTotal
  const sortProps = { onSortChange, orderBy }

  // Time in queue only means something for Ready, and only to WPC. Every other list
  // gets its age in days instead.
  const showInQueue = showWpcColumns && selected.bucket === BUCKET.READY

  // Who may do what from a row: see `rowPermissions`. A status column only when some
  // row on the page has a move.
  const { canAttach, canChangeStatus, canRemoveFiles } = permissions
  const canMove =
    canChangeStatus &&
    MOVE_BUCKETS.includes(selected.bucket) &&
    rows.some((ro) => dispositionsFor(ro.status).length > 0)
  const canSave = canChangeStatus || canAttach
  const toSave = rowsToSave(rows)

  const chooseStatus = (ro, disposition) => {
    updateEdit(ro, { disposition })
    // Outstanding List needs its form, which has required fields.
    if (disposition === DISPOSITION.OUTSTANDING) onOpenOutstanding(ro)
  }

  // No column of blanks: Ready ROs, for one, have no claim yet. The same goes for the
  // detail columns below: each shows only when some row on the page has something for it.
  const showClaimed = rows.some((ro) => Number(ro.totalClaimAmount) > 0 || writtenOff(ro) > 0)
  const showStatus = MIXED_STATUS_BUCKETS.includes(selected.bucket)
  const showBackIn = selected.bucket === BUCKET.RETURNED
  // Who has an RO on the Outstanding List: WPC reviewing, or waiting on the dealer.
  const reviewState = (ro) => (ro.status === 'On Outstanding List' ? ro.state : null)
  const showReview = rows.some(reviewState)
  // Not in Received either, for width: the actions show on the Outstanding list and on
  // the RO's own page. The Outstanding lists always have it, so the WPC review and dealer
  // review lists match even when one page has no open actions (2026-10-06).
  const showActions =
    REVIEW_BUCKETS.includes(selected.bucket) ||
    (selected.bucket !== BUCKET.RECEIVED && rows.some((ro) => openActions(ro).length > 0))

  const columns = [
    'ro',
    showStatus && 'status',
    showBackIn && 'backIn',
    showReview && 'review',
    showActions && 'actions',
    'age',
    showClaimed && 'claimed',
    canMove && 'moveTo',
    canChangeStatus && 'dealerClosed',
    'files',
    canSave && 'save',
  ].filter(Boolean)
  const tableMinWidth = columns.reduce((sum, column) => sum + COLUMN_WIDTHS[column], 0)

  return (
    // Beside the table (xl), no taller than it: placed in the page's stretched cell, so the
    // table alone sets the row's height, and the rows scroll inside (2026-10-12). Under the
    // table, no taller than the screen.
    <div className="flex max-h-[calc(100vh-7rem)] flex-col rounded-2xl bg-white shadow-sm lg:sticky lg:top-4 xl:absolute xl:inset-x-0 xl:top-0 xl:max-h-full">
      <div className="border-charcoal-100 flex items-start justify-between gap-3 border-b px-5 py-4">
        <div>
          <span
            className="inline-block rounded-full px-3 py-1 text-xs font-medium uppercase"
            style={{ backgroundColor: meta.fill, color: meta.ink }}
          >
            {meta.label}
          </span>
          <p className="text-charcoal-900 mt-2 text-sm font-semibold">
            {period?.label} ·{' '}
            {loading ? 'Loading…' : `${total.toLocaleString()} RO${total === 1 ? '' : 's'}`}
          </p>
          {lateCount > 0 && (
            <p className="mt-1 text-xs font-bold" style={{ color: SLA_LATE.ink }}>
              {lateCount} past SLA
            </p>
          )}
          {workQueueLinks[selected.bucket] && (
            <Link
              className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-blue-800 hover:underline"
              to={workQueueLinks[selected.bucket]}
            >
              Open in the Work Queue
              <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canSave && (
            <button
              className="flex h-8 items-center rounded bg-blue-800 px-3 text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={toSave.length === 0 || savingAll}
              onClick={async () => {
                setSavingAll(true)
                await commit(toSave)
                setSavingAll(false)
              }}
              type="button"
            >
              {savingAll ? 'Saving…' : `Save all${toSave.length ? ` (${toSave.length})` : ''}`}
            </button>
          )}
          <button
            aria-label="Close the repair order list"
            className="text-charcoal-400 hover:bg-charcoal-100 hover:text-charcoal-900 rounded-lg px-2 py-1 text-lg leading-none"
            onClick={onClose}
            type="button"
          >
            &times;
          </button>
        </div>
      </div>

      {/* Only when the ROs span more than one band. In Today, Week and Month every RO is
          under 30 days old, so a lone "0–30 days" band would just repeat the header. */}
      {showAging && bands && bands.length > 1 && (
        <div className="border-charcoal-100 flex border-b text-center text-xs">
          {bands.map(({ band, count }) => (
            <div className="flex-1 p-2" key={band.id} style={{ backgroundColor: band.tint }}>
              <span className="text-charcoal-900 block font-semibold tabular-nums">
                {count.toLocaleString()}
              </span>
              <span className="text-charcoal-500 block text-[10px]">{band.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* A stable gutter keeps the scrollbar beside the last column, not on top of it.
          `relative` keeps the chips' screen-reader inputs inside the scroll box. */}
      <div className="relative flex-1 overflow-auto [scrollbar-gutter:stable]">
        {loading && rows.length === 0 && (
          <p className="text-charcoal-400 px-5 py-12 text-center text-sm">Loading…</p>
        )}

        {!loading && rows.length === 0 && (
          <p className="text-charcoal-400 px-5 py-12 text-center text-sm">
            Nothing in this bucket for {period?.label.toLowerCase()}.
          </p>
        )}

        {rows.length > 0 && (
          <table
            className={mergeClassNames(
              'w-full table-fixed text-sm transition-opacity',
              // Keep the page on screen while the next one loads, dimmed.
              loading && 'opacity-50',
            )}
            style={{ minWidth: tableMinWidth }}
          >
            <colgroup>
              {columns.map((column) => (
                <col key={column} style={{ width: COLUMN_WIDTHS[column] }} />
              ))}
            </colgroup>
            <thead className="bg-background text-charcoal-700 sticky top-0 z-10 whitespace-nowrap text-[10px] uppercase tracking-widest">
              <tr>
                <SortHeader className="px-5 text-left" column="ro" {...sortProps}>
                  RO
                </SortHeader>
                {showStatus && (
                  <SortHeader className="px-2 text-left" column="status" {...sortProps}>
                    Status
                  </SortHeader>
                )}
                {showBackIn && <th className="p-2 text-left font-bold">Back in</th>}
                {showReview && <th className="p-2 text-left font-bold">Review</th>}
                {showActions && <th className="p-2 text-left font-bold">Actions needed</th>}
                <SortHeader
                  align="right"
                  // The last column when Claimed is hidden: give it the table's edge padding.
                  className="px-2 text-right"
                  column="age"
                  {...sortProps}
                >
                  {showInQueue ? 'In queue' : 'Age'}
                </SortHeader>
                {showClaimed && (
                  <SortHeader
                    align="right"
                    className="px-2 text-right"
                    column="claimed"
                    {...sortProps}
                  >
                    Claimed
                  </SortHeader>
                )}
                {canMove && <th className="p-2 text-left font-bold">Move to</th>}
                {canChangeStatus && (
                  <th className="p-2 text-left font-bold">
                    <abbr className="no-underline" title="Dealer closed">
                      DC
                    </abbr>
                  </th>
                )}
                <th className="p-2 text-left font-bold">Files</th>
                {canSave && (
                  <th className="py-2 pl-2 pr-5 font-bold">
                    <span className="sr-only">Save</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((ro) => {
                const late = showWpcColumns && isLate(ro)
                const actions = openActions(ro)
                const lost = writtenOff(ro)
                const edit = editFor(ro)
                const rowSaving = Boolean(saving[ro.id])

                return (
                  <tr
                    className={mergeClassNames(
                      'border-charcoal-100 border-b align-top',
                      late && 'bg-red-50',
                    )}
                    key={ro.id}
                  >
                    <td className="px-5 py-2">
                      <span className="flex items-center gap-1.5">
                        {/* The flag's column is there for whoever can set it; others see the
                            tag. */}
                        {!canChangeStatus && ro.dealerClosed && <DealerClosedTag />}
                        {/* An RO on the Outstanding List has its own page. One that never
                            reached it has no page, so it opens Manage Repair Order here. */}
                        {roHref(ro, dealerId) && (
                          // Opens a new tab, unlike the RO numbers that open the modal; the
                          // icon says which is which.
                          <a
                            className="text-charcoal-900 inline-flex items-center gap-1 font-mono hover:text-blue-800 hover:underline"
                            href={roHref(ro, dealerId)}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {ro.roNumber}
                            <ArrowTopRightOnSquareIcon
                              aria-label="Opens in a new tab"
                              className="text-charcoal-400 h-3 w-3 shrink-0"
                            />
                          </a>
                        )}
                        {!roHref(ro, dealerId) && onOpenRo && (
                          <button
                            className="text-charcoal-900 text-left font-mono hover:text-blue-800 hover:underline"
                            onClick={() => onOpenRo(ro)}
                            type="button"
                          >
                            {ro.roNumber}
                          </button>
                        )}
                        {!roHref(ro, dealerId) && !onOpenRo && (
                          <span className="text-charcoal-900 font-mono">{ro.roNumber}</span>
                        )}
                      </span>
                      <span className="text-charcoal-400 block text-[11px]">
                        {dayjs(ro.receivedAt).format('MM/DD/YY h:mm A')}
                      </span>
                      {late && (
                        <span
                          className="block text-[11px] font-bold"
                          style={{ color: SLA_LATE.ink }}
                        >
                          Past SLA
                        </span>
                      )}
                    </td>

                    {showStatus && (
                      <td className="p-2">
                        <div className="flex flex-wrap items-center gap-1">
                          {pillsFor(ro, selected.bucket).map((bucket) => (
                            <Pill bucket={bucket} key={bucket} />
                          ))}
                        </div>
                      </td>
                    )}

                    {showBackIn && (
                      <td className="text-charcoal-700 p-2 text-xs">
                        {RETURNED_TO[ro.status] || ''}
                      </td>
                    )}

                    {showReview && (
                      <td className="text-charcoal-700 p-2 text-xs">{reviewState(ro) || ''}</td>
                    )}

                    {showActions && (
                      <td className="p-2">
                        <span
                          className="text-charcoal-600 line-clamp-2 text-[11px]"
                          title={actions.join(' · ')}
                        >
                          {actions.join(' · ')}
                        </span>
                      </td>
                    )}

                    <td className="text-charcoal-500 whitespace-nowrap p-2 text-right text-xs tabular-nums">
                      {showInQueue
                        ? // Same wording as the Ready Queue: hours, then days past two days.
                          formatDuration(dayjs().diff(dayjs(ro.receivedAt), 'hour', true))
                        : `${daysSinceReceived(ro)} d`}
                    </td>

                    {showClaimed && (
                      <td className="p-2 text-right text-xs tabular-nums">
                        <span className="text-charcoal-700 block">
                          {money(ro.totalClaimAmount)}
                        </span>
                        {lost > 0 && (
                          <span className="block text-[11px]" style={{ color: SLA_LATE.ink }}>
                            {money(lost)} lost
                          </span>
                        )}
                      </td>
                    )}

                    {canMove && (
                      <td className="p-2">
                        <StatusChips
                          choice={edit.disposition}
                          disabled={rowSaving}
                          name={`next-status-${ro.id}`}
                          onChoose={(disposition) => chooseStatus(ro, disposition)}
                          options={dispositionsFor(ro.status)}
                          roNumber={ro.roNumber}
                          size="sm"
                        />
                      </td>
                    )}

                    {canChangeStatus && (
                      <td className="p-2">
                        <DealerClosedSwitch
                          checked={edit.dealerClosed}
                          disabled={rowSaving}
                          onChange={(dealerClosed) => updateEdit(ro, { dealerClosed })}
                          roNumber={ro.roNumber}
                        />
                      </td>
                    )}

                    <td className="p-2">
                      <AttachmentsPopover
                        canAdd={canAttach}
                        canRemove={canRemoveFiles}
                        disabled={rowSaving}
                        files={ro.files}
                        filesToDelete={edit.filesToDelete}
                        newFiles={edit.newFiles}
                        onChange={(patch) => updateEdit(ro, patch)}
                        roNumber={ro.roNumber}
                        size="sm"
                      />
                    </td>

                    {canSave && (
                      <td className="py-2 pl-2 pr-5 text-right">
                        <RowSaveButton
                          changed={savesInPlace(ro, edit)}
                          onSave={() => commit([ro])}
                          roNumber={ro.roNumber}
                          saving={rowSaving}
                          size="sm"
                        />
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > pageSize && (
        <div className="border-charcoal-100 border-t px-3 py-1">
          <Pagination
            currentPage={page}
            itemsPerPage={pageSize}
            onPageChange={onPageChange}
            totalItems={total}
          />
        </div>
      )}
    </div>
  )
}

RoListPanel.propTypes = {
  agingCounts: PropTypes.arrayOf(
    PropTypes.shape({ band: PropTypes.object, count: PropTypes.number }),
  ),
  dealerId: PropTypes.string,
  lateTotal: PropTypes.number,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  // Opens the Outstanding List form for an RO whose Outstanding List chip was picked.
  onOpenOutstanding: PropTypes.func.isRequired,
  // Opens Manage Repair Order for an RO with no Outstanding List page. Null keeps the link.
  onOpenRo: PropTypes.func,
  // What this user may do from a row; see `rowPermissions`.
  permissions: PropTypes.shape({
    canAttach: PropTypes.bool,
    canChangeStatus: PropTypes.bool,
    canRemoveFiles: PropTypes.bool,
  }).isRequired,
  // The page's staged row edits, from `useRowEdits`.
  rowEdits: PropTypes.shape({
    commit: PropTypes.func,
    editFor: PropTypes.func,
    rowsToSave: PropTypes.func,
    saving: PropTypes.object,
    updateEdit: PropTypes.func,
  }).isRequired,
  // The Work Queue tab that works each list, by bucket. Empty hides the links.
  workQueueLinks: PropTypes.objectOf(PropTypes.string),
  orderBy: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.shape({ bucket: PropTypes.string, periodId: PropTypes.string }),
  // Names the admin review list; see `bucketMeta`.
  selfManaged: PropTypes.bool,
  showWpcColumns: PropTypes.bool,
  total: PropTypes.number,
}

export default RoListPanel
